import StoreTableModel from 'models/store/StoreTableModel';

export type StoreSortField = 'store' | 'scratcherQty' | 'scratcherSales' | 'lottoSales' | 'totalPayouts' | 'finalVariance';
export type StoreSortDirection = 'asc' | 'desc';

export interface StoreSortState {
  field: StoreSortField;
  direction: StoreSortDirection;
}

interface IndexedRow {
  row: any;
  originalIndex: number;
}

const getVarianceValue = (row: any, field: StoreSortField): number | string | null => {
  const storeModel = StoreTableModel.fromServerData(row);

  if (field === 'store') {
    return storeModel.name.toLowerCase();
  }

  const variance = row?.reconciliation_variance;
  const absoluteVariance = (value: number | null | undefined): number | null =>
    value == null ? null : Math.abs(Number(value));

  if (field === 'scratcherQty') {
    return absoluteVariance(variance?.scratcher?.quantity_variance);
  }

  if (field === 'scratcherSales') {
    return absoluteVariance(variance?.scratcher?.revenue_variance);
  }

  if (field === 'lottoSales') {
    return absoluteVariance(variance?.lotto_sales_variance);
  }

  if (field === 'totalPayouts') {
    return absoluteVariance(variance?.payout_variance);
  }

  return absoluteVariance(variance?.total_variance);
};

const compareValues = (
  leftValue: number | string | null,
  rightValue: number | string | null,
  direction: StoreSortDirection
): number => {
  if (leftValue === null && rightValue === null) return 0;
  if (leftValue === null) return 1;
  if (rightValue === null) return -1;

  let result = 0;
  if (typeof leftValue === 'string' && typeof rightValue === 'string') {
    result = leftValue.localeCompare(rightValue);
  } else {
    result = Number(leftValue) - Number(rightValue);
  }

  return direction === 'asc' ? result : -result;
};

export const sortStoreRows = (rows: any[], sortState: StoreSortState): any[] => {
  const indexedRows: IndexedRow[] = rows.map((row, index) => ({ row, originalIndex: index }));

  indexedRows.sort((left, right) => {
    const leftValue = getVarianceValue(left.row, sortState.field);
    const rightValue = getVarianceValue(right.row, sortState.field);
    const comparison = compareValues(leftValue, rightValue, sortState.direction);

    if (comparison !== 0) return comparison;
    return left.originalIndex - right.originalIndex;
  });

  return indexedRows.map((item) => item.row);
};
